import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL } from 'constants/offer'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import getLPCRegionLOS from 'lib/offer/getLPCRegionLOS'
import getOfferStaysFrom from 'lib/offer/getOfferStaysFrom'
import PriceRowPriceLoadingSkeleton from 'LuxKit/PricePoints/PriceRowPriceLoadingSkeleton'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { useOfferPackagePrice } from 'hooks/Offers/useOfferPriceNew'
import getOfferDurationText from 'lib/offer/getOfferDurationText'
import useOffersBestPrices from 'hooks/useOfferBestPrices'
import useSearchOfferBurnPricing from 'hooks/useSearchOfferBurnPricing'
import { EmptyObject } from 'lib/object/objectUtils'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { useAppSelector } from 'hooks/reduxHooks'
import { isHotelPackage } from 'lib/offer/offerTypes'
import { isDisplayablePrice } from 'lib/offer/pricingVisibility'
import LuxPlusOfferVoucherCallout from 'luxPlus/components/LuxPlusOfferVoucherCallout'
import Group from 'components/utils/Group'
import LoyaltyBurnPriceDisplay from 'loyalty/components/LoyaltyBurnPriceDisplay'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import getHotelEncryptedMargin from 'loyalty/lib/margin/getHotelEncryptedMargin'
import useOfferTilePriceBlockExperiment from 'hooks/Experiments/useOfferTilePriceBlockExperiment'
import OfferTilePriceBlockSavingsLabel from 'components/OfferList/OfferListTiles/Support/OfferTilePriceBlockSavingsLabel'
import OfferTilePriceBlockCaption from 'components/OfferList/OfferListTiles/Support/OfferTilePriceBlockCaption'

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary | App.TourOffer | App.TourOfferSummary | App.VillaOffer | App.VillaOfferSummary
  selectedDatesAndRoomsFilter?: App.OfferListFilters
  filters?: App.OfferListFilters
}

function LEOfferPricing(props: Props) {
  const {
    offer,
    selectedDatesAndRoomsFilter = EmptyObject,
    filters,
  } = props
  const intl = useIntl()
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const bestPkg = useOffersBestPrices(offer, selectedDatesAndRoomsFilter)
  const pkg = bestPkg?.bestPricePackage ?? offer.lowestPricePackage!
  const {
    default: {
      saleUnit,
      price,
      value,
      discountPercent,
    },
    member: memberPricing,
  } = useOfferPackagePrice(pkg, offer)

  const valueToDisplay = canViewLuxPlusBenefits && memberPricing?.value ? memberPricing.value : value
  const discountToDisplay = canViewLuxPlusBenefits && memberPricing ? memberPricing.discountPercent : discountPercent

  const shouldShowRate = !offer.isDiscountPillHidden && discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const shouldShowValue = pkg.value > 0 && price && value > price && pkg.shouldDisplayValue &&
    (offer.type == OFFER_TYPE_HOTEL || (isHotelPackage(pkg) && !pkg.roomRate?.inclusionsHideValue))

  const staysFrom = getOfferStaysFrom(offer)
  const LPCRegionLOS = getLPCRegionLOS(offer)
  const showStaysFromLabel = staysFrom && offer.type === OFFER_TYPE_ALWAYS_ON && LPCRegionLOS && staysFrom < pkg.duration
  const durationText = useMemo(() => getOfferDurationText(intl, offer, pkg), [intl, offer, pkg])
  const preferPointsPricing = !!filters?.viewPriceInPoints
  const {
    priceInPoints,
    memberPriceInPoints,
    pointsToBurn,
    pointsOnly,
    hideZeroCash,
    shouldShowBurn,
  } = useSearchOfferBurnPricing({
    price,
    memberPrice: memberPricing?.price,
    loyaltyProductType: getLoyaltyProductType(offer),
    mx: getHotelEncryptedMargin(offer.type, pkg),
    viewPriceInPoints: preferPointsPricing,
    disabled: !price,
  })
  // When the caller asked for points pricing, never fall back to cash — that flash is what
  // makes spend-points carousels feel jittery if the burn cache misses or is cleared.
  const isBurnPricingLoading = preferPointsPricing && !shouldShowBurn
  const priceBlockVariation = useOfferTilePriceBlockExperiment(offer, undefined, isDisplayablePrice(price) && !preferPointsPricing && !shouldShowBurn)

  // An offer can reach a tile with no usable price: an always-on offer whose cached lowest option
  // has emptied out arrives with every package at 0, and `getLowestPricePackage` then leads with a
  // zero-priced package rather than returning nothing. Rendering that verbatim prints "A$0". Drop
  // the pricing block instead, matching how the search tiles handle an offer with no price.
  if (!isDisplayablePrice(price)) return null

  return (<Group direction="vertical" gap={priceBlockVariation ? 0 : 4}>
    <Group direction="vertical" gap={4} noDisplayWhenEmpty>
      <LuxPlusOfferVoucherCallout
        offer={offer}
        mode="search"
        align="start"
      />
      {priceBlockVariation && <OfferTilePriceBlockSavingsLabel
        offer={offer}
        price={price}
        memberPrice={memberPricing?.price}
        discountPercentage={shouldShowValue && shouldShowRate ? discountToDisplay : undefined}
        variation={priceBlockVariation}
        offerConfig={offer.luxPlus}
      />}
    </Group>
    <OfferTilePriceBlockCaption
      priceBlockExperimentVariation={priceBlockVariation}
      duration={pkg.duration || LPCRegionLOS || undefined}
      durationLabel={pkg.duration ? undefined : offer.tileDurationLabel}
      saleUnit={saleUnit}
    >{durationText}
    </OfferTilePriceBlockCaption>
    {isBurnPricingLoading && <PriceRowPriceLoadingSkeleton size="L" />}
    {shouldShowBurn && <LoyaltyBurnPriceDisplay
      totalPrice={price}
      pointsToBurn={pointsToBurn}
      priceInPoints={priceInPoints}
      memberPrice={memberPricing?.price}
      memberPriceInPoints={memberPriceInPoints}
      size="L"
      saleUnit={saleUnit}
      pointsOnly={pointsOnly}
      hideZeroCash={hideZeroCash}
      offerConfig={offer.luxPlus}
    />}
    {!preferPointsPricing && !shouldShowBurn && <LuxPlusPriceStack
      price={price}
      memberPrice={memberPricing?.price}
      size="L"
      saleUnit={saleUnit}
      offerConfig={offer.luxPlus}
      presentation={priceBlockVariation ? 'price-block-experiment' : undefined}
      testid="main-offer-pricing"
    />}
    {shouldShowValue && <PriceRowValueDiscountWithCaption
      weight={priceBlockVariation ? 'normal' : undefined}
      colour={priceBlockVariation ? 'light-foreground-alt' : undefined}
      size="L"
      originalValue={Math.ceil(valueToDisplay)}
      discountPercentage={priceBlockVariation ? undefined : (shouldShowRate ? discountToDisplay : undefined)}
      data-testid="le-offer-pricing"
    />}
    {staysFrom && showStaysFromLabel && <PriceRowCaption>
      <FormattedMessage
        id="7PFPr3tSopW8Hgmmb_Dy"
        defaultMessage="Stays from {staysFrom, plural, one {# night} other {# nights}} available"
        description="Caption indicating minimum stay duration available for an always-on offer"
        values={{ staysFrom }}
      />
    </PriceRowCaption>}
    {!!pkg.roomOccupancy && offer.type === 'tour' && <PriceRowCaption>
      {pkg.roomOccupancy}
    </PriceRowCaption>}
  </Group>)
}

export default LEOfferPricing
